import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Grid,
  CircularProgress,
  IconButton,
  Stack,
  Alert
} from "@mui/material";
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../store";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { GetTournamentList } from "../../store/user/actions";
import { CreateTournament, DeclareResultTournament, StartTournament, ClearTournament } from "../../store/user/actions";


const TournamentPanel: React.FC = () => {
  const dispatch = useDispatch<any>();
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTournament, setEditingTournament] = useState<any | null>(null);
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    start_date: new Date(),
    end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days from now
    prize: "",
    max_participants: "",
    description: ""
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  type DateTimeEpoch = string | null;
  // Only required fields
  const [formFields, setFormFields] = useState({
    maxPlayers: 0,
    minLevel: 0,
    maxLevel: 0,
    schema: [] as string[],
    tournamentId: 0,
  });
  const [dateTimes, setDateTimes] = useState({
    regStart: null as string | null,
    regEnd: null as string | null,
    start: null as string | null,
    end: null as string | null,
  });
  const [epochs, setEpochs] = useState({
    regStart: null as number | null,
    regEnd: null as number | null,
    start: null as number | null,
    end: null as number | null,
  });

  const userModule = useSelector((state: AppState) => state.userModule);
  const gameStatus = userModule.gameStatus;
  const tournamentsFromStore = gameStatus?.tournaments ?? [];
  const [tournaments, setTournaments] = useState<Array<any>>(tournamentsFromStore);

  useEffect(() => {
    setTournaments(tournamentsFromStore);
  }, [tournamentsFromStore]);

  useEffect(() => {
    dispatch(GetTournamentList());
  }, [dispatch]);

  const handleOpenDialog = (tournament: any | null = null) => {
    if (tournament) {
      setEditingTournament(tournament);
      setFormFields({
        maxPlayers: tournament.rules.max_players || 0,
        minLevel: tournament.rules.min_level || 0,
        maxLevel: tournament.rules.max_level || 0,
        schema: Array.isArray(tournament.rules.schema) ? tournament.rules.schema : [],
        tournamentId: tournament.id || 0,
      });
      setDateTimes({
        regStart: tournament.reg_start ? new Date(tournament.reg_start * 1000).toISOString().slice(0, 16) : "",
        regEnd: tournament.reg_end ? new Date(tournament.reg_end * 1000).toISOString().slice(0, 16) : "",
        start: tournament.start_time ? new Date(tournament.start_time * 1000).toISOString().slice(0, 16) : "",
        end: tournament.end_time ? new Date(tournament.end_time * 1000).toISOString().slice(0, 16) : "",
      });
      setEpochs({
        regStart: tournament.reg_start || null,
        regEnd: tournament.reg_end || null,
        start: tournament.start_time || null,
        end: tournament.end_time || null,
      });
    } else {
      setEditingTournament(null);
      setFormFields({
        maxPlayers: 0,
        minLevel: 0,
        maxLevel: 0,
        schema: [],
        tournamentId: 0,
      });
      setDateTimes({
        regStart: "",
        regEnd: "",
        start: "",
        end: "",
      });
      setEpochs({
        regStart: null,
        regEnd: null,
        start: null,
        end: null,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setError("");
  };

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (["maxPlayers", "minLevel", "maxLevel", "tournamentId"].includes(name)) {
      setFormFields((prev) => ({ ...prev, [name]: Number(value) }));
    } else {
      setFormFields((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleDateTimeChange = (field: keyof typeof dateTimes, value: string) => {
    setDateTimes((prev) => ({ ...prev, [field]: value }));
    const epoch = value ? Math.floor(new Date(value).getTime() / 1000) : null;
    setEpochs((prev) => ({ ...prev, [field]: epoch }));
  };

  const handleSchemaToggle = (schema: string) => {
    setFormFields((prev) => ({
      ...prev,
      schema: prev.schema.includes(schema)
        ? prev.schema.filter((s) => s !== schema)
        : [...prev.schema, schema],
    }));
  };

  const handleSubmit = async () => {
    setError("");
    setSuccess("");
    if (
      !formFields.maxPlayers ||
      !formFields.minLevel ||
      !formFields.maxLevel ||
      !formFields.schema.length ||
      !formFields.tournamentId ||
      !dateTimes.regStart ||
      !dateTimes.regEnd ||
      !dateTimes.start ||
      !dateTimes.end
    ) {
      setError("Please fill all required fields");
      return;
    }
    setLoading(true);
    try {
      await dispatch(
        CreateTournament(
          formFields.tournamentId,
          epochs.regStart,
          epochs.regEnd,
          epochs.start,
          epochs.end,
          formFields.minLevel,
          formFields.maxLevel,
          formFields.schema,
          formFields.maxPlayers
        )
      );
      setSuccess("Tournament created successfully");
      dispatch(GetTournamentList());
      handleCloseDialog();
      setTimeout(() => setSuccess(""), 3000);
    } catch {
      setError("Error saving tournament data");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    // Early return if user cancels confirmation
    if (!window.confirm("Are you sure you want to delete this tournament?")) {
      return;
    }

    // Set loading state for better UX
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // Delete the tournament
      await dispatch(ClearTournament(id));
      
      // Refresh the tournament list
      await dispatch(GetTournamentList());
      
      // Show success message
      setSuccess("Tournament deleted successfully");
      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      // Handle errors gracefully
      console.error("Error deleting tournament:", error);
      setError("Failed to delete tournament. Please try again.");
      setTimeout(() => setError(""), 5000);
    } finally {
      // Always reset loading state
      setLoading(false);
    }
  };

  const rulesToString = (rules: { [key: string]: any }): string => {
    const keyLabels: Record<string, string> = {
      max_level: "Max Level",
      max_players: "Max Players",
      min_level: "Min Level",
      schema: "Schemas",
    };
    return Object.entries(rules)
      .map(([key, value]) => {
        const label = keyLabels[key] ?? key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
        if (key === "schema" && Array.isArray(value)) {
          return `${label}: ${value.join(", ")}`;
        }
        return `${label}: ${value}`;
      })
      .join(", ");
  };

  const entriesSummary = (entries: Array<{ player: string }>): string => {
    if (!Array.isArray(entries) || entries.length === 0) return "Count: 0, Players: ";
    const players = entries.map(e => e.player).join(", ");
    return `Count: ${entries.length}, Players: ${players}`;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ color: 'white' }}>
            Tournament Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Create Tournament
          </Button>
        </Box>

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Paper sx={{ bgcolor: 'rgba(0, 0, 0, 0.6)', borderRadius: 2 }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && tournaments.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" sx={{ color: 'white' }}>
                No tournaments found. Create one to get started.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: 'white' }}>ID</TableCell>
                    <TableCell sx={{ color: 'white' }}>Reg Start Date</TableCell>
                    <TableCell sx={{ color: 'white' }}>Reg End Date</TableCell>
                    <TableCell sx={{ color: 'white' }}>Start Date</TableCell>
                    <TableCell sx={{ color: 'white' }}>End Date</TableCell>
                    <TableCell sx={{ color: 'white' }}>Pool</TableCell>
                    <TableCell sx={{ color: 'white' }}>Rules</TableCell>
                    <TableCell sx={{ color: 'white' }}>Participants</TableCell>
                    <TableCell sx={{ color: 'white' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tournaments.map((tournament) => (
                    <TableRow key={tournament.id}>
                      <TableCell sx={{ color: 'white' }}>{tournament.id}</TableCell>
                      <TableCell sx={{ color: 'white' }}>
                        {new Date(tournament.reg_start * 1000).toLocaleString()}
                      </TableCell>
                      <TableCell sx={{ color: 'white' }}>
                        {new Date(tournament.reg_end * 1000).toLocaleString()}
                      </TableCell>
                      <TableCell sx={{ color: 'white' }}>
                        {new Date(tournament.start_time * 1000).toLocaleString()}
                      </TableCell>
                      <TableCell sx={{ color: 'white' }}>
                        {new Date(tournament.end_time * 1000).toLocaleString()}
                      </TableCell>
                      <TableCell sx={{ color: 'white' }}>{tournament.total_fees}</TableCell>
                      <TableCell sx={{ color: 'white' }}>{rulesToString(tournament.rules)}</TableCell>
                      <TableCell sx={{ color: 'white' }}>
                        {entriesSummary(tournament.entries)}
                      </TableCell>
                      <TableCell>
                        <Grid container spacing={1}>
                          <Grid item xs={6} sm={3}>
                            <Box sx={{ width: '100%' }}>
                              <IconButton
                                size="small"
                                color="primary"
                                aria-label="Edit Tournament"
                                onClick={() => handleOpenDialog(tournament)}
                                sx={{ width: '100%' }}
                              >
                                <EditIcon />
                              </IconButton>
                            </Box>
                          </Grid>

                          <Grid item xs={6} sm={3}>
                            <Box sx={{ width: '100%' }}>
                              <IconButton
                                size="small"
                                color="error"
                                aria-label="Delete Tournament"
                                onClick={() => handleDelete(tournament.id)}
                                sx={{ width: '100%' }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </Grid>

                          <Grid item xs={6} sm={3}>
                            <Box sx={{ width: '100%' }}>
                              <IconButton
                                size="small"
                                color="success"
                                aria-label="Start Match"
                                title="Start Match"
                                onClick={() => {
                                  if (window.confirm(`Are you sure you want to start Tournament ID: ${tournament.id}?`)) {
                                    dispatch(StartTournament(tournament.id));
                                  }
                                }}
                                sx={{ width: '100%' }}
                              >
                                <PlayCircleFilledIcon />
                              </IconButton>
                            </Box>
                          </Grid>

                          <Grid item xs={6} sm={3}>
                            <Box sx={{ width: '100%' }}>
                              <IconButton
                                size="small"
                                color="secondary"
                                aria-label="Declare Result"
                                title="Declare Result"
                                onClick={() => {
                                  if (window.confirm(`Are you sure you want to declare result for Tournament ID: ${tournament.id}?`)) {
                                    dispatch(DeclareResultTournament(tournament.id));
                                  }
                                }}
                                sx={{ width: '100%' }}
                              >
                                <DoneAllIcon />
                              </IconButton>
                            </Box>
                          </Grid>
                        </Grid>

                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={{
          sx: {
            bgcolor: 'rgba(33, 33, 33, 0.98)',
            borderRadius: 3,
            boxShadow: '0 8px 32px 0 rgba(25,118,210,0.18)',
            p: 2,
            height: '60vh'
          }
        }}>
          <DialogTitle sx={{
            bgcolor: 'linear-gradient(90deg, #1976d2 60%, #1565c0 100%)',
            color: 'white',
            fontWeight: 700,
            fontSize: 22,
            letterSpacing: 1,
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            mb: 1
          }}>
            {editingTournament ? 'Edit Tournament' : 'Create New Tournament'}
          </DialogTitle>
          <DialogContent sx={{ pb: 0 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <form autoComplete="off" onSubmit={e => { e.preventDefault(); handleSubmit(); }}>
              <Grid container spacing={2}>
                <Grid item xs={10}>
                  <label style={{ color: 'white', fontWeight: 500, fontSize: 15, marginBottom: 4, display: 'block' }}>
                    Max Players
                  </label>
                  <input
                    type="number"
                    name="maxPlayers"
                    min={1}
                    required
                    placeholder="Max Players"
                    value={formFields.maxPlayers}
                    onChange={handleFieldChange}
                    style={{ width: '100%', padding: 12, borderRadius: 6, border: '1.5px solid #1976d2', background: '#181c24', color: 'white', textAlign: 'left', fontSize: 16, marginBottom: 2 }}
                  />
                </Grid>
                <Grid item xs={5}>
                  <label style={{ color: 'white', fontWeight: 500, fontSize: 15, marginBottom: 4, display: 'block' }}>
                    Min Level
                  </label>
                  <input
                    type="number"
                    name="minLevel"
                    min={1}
                    required
                    placeholder="Min Level"
                    value={formFields.minLevel}
                    onChange={handleFieldChange}
                    style={{ width: '50%', padding: 12, borderRadius: 6, border: '1.5px solid #1976d2', background: '#181c24', color: 'white', textAlign: 'left', fontSize: 16, marginBottom: 2 }}
                  />
                </Grid>
                <Grid item xs={5}>
                  <label style={{ color: 'white', fontWeight: 500, fontSize: 15, marginBottom: 4, display: 'block' }}>
                    Max Level
                  </label>
                  <input
                    type="number"
                    name="maxLevel"
                    min={1}
                    required
                    placeholder="Max Level"
                    value={formFields.maxLevel}
                    onChange={handleFieldChange}
                    style={{ width: '50%', padding: 12, borderRadius: 6, border: '1.5px solid #1976d2', background: '#181c24', color: 'white', textAlign: 'left', fontSize: 16, marginBottom: 2 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <label style={{ color: 'white', fontWeight: 500, fontSize: 15, marginBottom: 4, display: 'block' }}>
                    Select Schemas
                  </label>
                  <Box
                    sx={{
                      background: '#181c24',
                      border: '1.5px solid #1976d2',
                      borderRadius: 1,
                      p: 1,
                      mb: 1,
                      color: 'white',
                      minHeight: 56,
                    }}
                  >
                    {['og.deer', 'fawn', 'abyssal'].map((schema) => (
                      <Button
                        key={schema}
                        variant={formFields.schema.includes(schema) ? 'contained' : 'outlined'}
                        color="primary"
                        size="small"
                        sx={{
                          m: 0.5,
                          background: formFields.schema.includes(schema)
                            ? 'linear-gradient(90deg, #1976d2 60%, #1565c0 100%)'
                            : 'none',
                          color: 'white',
                          borderColor: '#1976d2',
                          fontWeight: 600,
                          '&:hover': {
                            background: formFields.schema.includes(schema)
                              ? 'linear-gradient(90deg, #1976d2 60%, #1565c0 100%)'
                              : '#1976d2',
                          },
                        }}
                        onClick={() => handleSchemaToggle(schema)}
                      >
                        {schema}
                      </Button>
                    ))}
                    <Box sx={{ mt: 1, fontSize: 13, color: '#90caf9' }}>
                      {formFields.schema.length === 0
                        ? 'Select one or more schemas above'
                        : `Selected: ${formFields.schema.join(', ')}`}
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={10}>
                  <label style={{ color: 'white', fontWeight: 500, fontSize: 15, marginBottom: 4, display: 'block' }}>
                    Tournament ID
                  </label>
                  <input
                    type="number"
                    name="tournamentId"
                    required
                    placeholder="Tournament ID"
                    value={formFields.tournamentId}
                    onChange={handleFieldChange}
                    style={{ width: '100%', padding: 12, borderRadius: 6, border: '1.5px solid #1976d2', background: '#181c24', color: 'white', textAlign: 'left', fontSize: 16, marginBottom: 2 }}
                  />
                </Grid>
                <Grid item xs={10}>
                  <label style={{ color: 'white', fontWeight: 500, fontSize: 15 }}>
                    Registration Start Date & Time
                    <input
                      type="datetime-local"
                      name="regStart"
                      value={dateTimes.regStart || ''}
                      onChange={e => handleDateTimeChange('regStart', e.target.value)}
                      style={{ width: '100%', padding: 12, borderRadius: 6, border: '1.5px solid #1976d2', background: '#181c24', color: 'white', marginTop: 4, fontSize: 16 }}
                    />
                    {dateTimes.regStart && (
                      <span style={{ marginLeft: 8, color: '#90caf9', fontSize: 13 }}>
                        (Epoch: {epochs.regStart})
                      </span>
                    )}
                  </label>
                </Grid>
                <Grid item xs={10}>
                  <label style={{ color: 'white', fontWeight: 500, fontSize: 15 }}>
                    Registration End Date & Time
                    <input
                      type="datetime-local"
                      name="regEnd"
                      value={dateTimes.regEnd || ''}
                      onChange={e => handleDateTimeChange('regEnd', e.target.value)}
                      style={{ width: '100%', padding: 12, borderRadius: 6, border: '1.5px solid #1976d2', background: '#181c24', color: 'white', marginTop: 4, fontSize: 16 }}
                    />
                    {dateTimes.regEnd && (
                      <span style={{ marginLeft: 8, color: '#90caf9', fontSize: 13 }}>
                        (Epoch: {epochs.regEnd})
                      </span>
                    )}
                  </label>
                </Grid>
                <Grid item xs={10}>
                  <label style={{ color: 'white', fontWeight: 500, fontSize: 15 }}>
                    Tournament Start Date & Time
                    <input
                      type="datetime-local"
                      name="start"
                      value={dateTimes.start || ''}
                      onChange={e => handleDateTimeChange('start', e.target.value)}
                      style={{ width: '100%', padding: 12, borderRadius: 6, border: '1.5px solid #1976d2', background: '#181c24', color: 'white', marginTop: 4, fontSize: 16 }}
                    />
                    {dateTimes.start && (
                      <span style={{ marginLeft: 8, color: '#90caf9', fontSize: 13 }}>
                        (Epoch: {epochs.start})
                      </span>
                    )}
                  </label>
                </Grid>
                <Grid item xs={10}>
                  <label style={{ color: 'white', fontWeight: 500, fontSize: 15 }}>
                    Tournament End Date & Time
                    <input
                      type="datetime-local"
                      name="end"
                      value={dateTimes.end || ''}
                      onChange={e => handleDateTimeChange('end', e.target.value)}
                      style={{ width: '100%', padding: 12, borderRadius: 6, border: '1.5px solid #1976d2', background: '#181c24', color: 'white', marginTop: 4, fontSize: 16 }}
                    />
                    {dateTimes.end && (
                      <span style={{ marginLeft: 8, color: '#90caf9', fontSize: 13 }}>
                        (Epoch: {epochs.end})
                      </span>
                    )}
                  </label>
                </Grid>
                <Grid item xs={10}>
                  <button
                    type="submit"
                    style={{ width: '100%', padding: 14, borderRadius: 6, background: 'linear-gradient(90deg, #1976d2 60%, #1565c0 100%)', color: 'white', border: 'none', fontWeight: 'bold', fontSize: 18, letterSpacing: 1, marginTop: 8, boxShadow: '0 2px 8px rgba(25,118,210,0.15)' }}
                  >
                    {editingTournament ? 'Save' : 'Create Tournament'}
                  </button>
                </Grid>
              </Grid>
            </form>
          </DialogContent>
          <DialogActions sx={{ bgcolor: 'rgba(33,33,33,0.98)', borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }}>
            <Button onClick={handleCloseDialog} sx={{ color: '#1976d2', fontWeight: 600, fontSize: 16 }}>Cancel</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default TournamentPanel;